import React, { useState, useRef, Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import reduce from 'lodash/reduce';
import merge from 'lodash/merge';
import { DragDropContext, DragSource, DropTarget } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import cn from 'classnames';
import { Box, Hidden, Button, Menu, MenuItem, Popover, CircularProgress, IconButton, Tooltip } from '@material-ui/core';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import identity from 'lodash/identity';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
import each from 'lodash/each';
import remove from 'lodash/remove';
import findKey from 'lodash/findKey';
import moment from 'moment-timezone';
import find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import { ErrorBoundary } from '@scoir/error-reporter';
import isEqual from 'lodash/isEqual';
import union from 'lodash/union';
import isFunction from 'lodash/isFunction';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown, faVideoCamera, faUser } from '@fortawesome/pro-solid-svg-icons';
import isUndefined from 'lodash/isUndefined';
import CollegeCrest from '@scoir/college-crest';
import size from 'lodash/size';
import { DismissibleDialog } from '@scoir/modals';
import moment$1 from 'moment';
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import times from 'lodash/times';
import cloneDeep from 'lodash/cloneDeep';
import memoize from 'lodash/memoize';
import isArray from 'lodash/isArray';

var Context = /*#__PURE__*/React.createContext({
  configs: []
});

class CalendarConfigProvider extends React.Component {
  static propTypes = {
    configs: PropTypes.array,
    children: PropTypes.any
  };
  render() {
    return /*#__PURE__*/React.createElement(Context.Provider, {
      value: {
        configs: this.props.configs
      }
    }, this.props.children);
  }
}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

var withConfigForTime = (WrappedComponent => {
  class HoC extends React.Component {
    render() {
      return /*#__PURE__*/React.createElement(Context.Consumer, null, value => /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, this.props, {
        configFn: (start, end) => reduce(value.configs, (result, configDefinition) => {
          const {
            matches,
            ...rest
          } = configDefinition;
          if (matches(start, end)) {
            return merge(result, rest);
          }
          return result;
        }, {
          theme: {},
          config: {}
        })
      })));
    }
  }
  HoC.displayName = `withCalendarConfig(${WrappedComponent.displayName || WrappedComponent.name})`;
  return HoC;
});

var Provider = DragDropContext(HTML5Backend)(CalendarConfigProvider);

class ControlPanel extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    children: PropTypes.node.isRequired
  };
  render() {
    return /*#__PURE__*/React.createElement("div", {
      className: cn('rc-control-panel-comp', this.props.className)
    }, this.props.children);
  }
}

const LENS_TYPES = [{
  label: 'Week',
  value: 'week'
}, {
  label: 'Month',
  value: 'month'
}, {
  label: 'List',
  value: 'list'
}];
const LENS_VALUES = LENS_TYPES.map(lens => lens.value);
const LensControl = _ref => {
  let {
    lens = 'week',
    onLensChange,
    isVisible = LENS_VALUES
  } = _ref;
  const [anchorEl, setAnchorEl] = useState(null);
  const visibleLens = filter(LENS_TYPES, lens => includes(isVisible, lens.value));
  const currentLens = filter(LENS_TYPES, l => l.value === lens);
  return /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Hidden, {
    mdDown: true
  }, visibleLens.map(lens => /*#__PURE__*/React.createElement(Button, {
    key: lens.value,
    color: "primary",
    variant: currentLens[0].value === lens.value ? 'contained' : 'text',
    onClick: () => onLensChange(lens.value)
  }, lens.label))), /*#__PURE__*/React.createElement(Hidden, {
    lgUp: true
  }, /*#__PURE__*/React.createElement(Button, {
    "aria-controls": "calendar-lens-control-menu",
    "aria-haspopup": "true",
    onClick: e => setAnchorEl(e.currentTarget),
    variant: "contained",
    color: "primary"
  }, currentLens[0].label), /*#__PURE__*/React.createElement(Menu, {
    id: "calendar-lens-control-menu",
    anchorEl: anchorEl,
    keepMounted: true,
    open: Boolean(anchorEl),
    onClose: () => setAnchorEl(null)
  }, visibleLens.map(lens => /*#__PURE__*/React.createElement(MenuItem, {
    key: lens.value,
    onClick: () => {
      onLensChange(lens.value);
      setAnchorEl(null);
    }
  }, lens.label)))));
};
LensControl.propTypes = {
  lens: PropTypes.oneOf(LENS_VALUES),
  onLensChange: PropTypes.func.isRequired,
  isVisible: PropTypes.arrayOf(PropTypes.oneOf(LENS_VALUES))
};

const abstractEventType = 'rcw-cal-event';

function getPlacement(renderDate) {
  let day = renderDate.day();
  if (day < 4) {
    return 'right';
  }
  return 'left';
}
const AbstractEvent = props => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const {
    renderDate,
    draggedItem,
    event: evt,
    summary,
    details,
    connectDragSource,
    onMove
  } = props;
  const connectFn = onMove ? connectDragSource : identity;
  const orient = getPlacement(renderDate);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    ref: ref,
    role: "button",
    tabIndex: "0",
    onClick: () => setOpen(!open),
    className: cn('abstract-event-comp', {
      dragging: draggedItem,
      'other-dragging': draggedItem && evt.Id !== draggedItem.id
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: "summary-wrapper"
  }, connectFn(summary))), /*#__PURE__*/React.createElement(Popover, {
    open: open,
    anchorEl: ref.current,
    anchorOrigin: {
      vertical: 'top',
      horizontal: orient
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: orient === 'left' ? 'right' : 'left'
    },
    onClose: () => setOpen(false)
  }, details));
};
AbstractEvent.propTypes = {
  event: PropTypes.object.isRequired,
  summary: PropTypes.node.isRequired,
  details: PropTypes.node.isRequired,
  renderDate: PropTypes.object.isRequired,
  onMove: PropTypes.func,
  connectDragSource: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  draggedItem: PropTypes.object
};
var index$1 = DragSource(abstractEventType, {
  beginDrag: props => ({
    id: props.event.Id
  }),
  endDrag: (props, monitor) => {
    const result = monitor.getDropResult();
    if (result) {
      props.onMove(result);
    }
  }
}, (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  isDragging: monitor.isDragging(),
  draggedItem: monitor.getItem()
}))(AbstractEvent);

const src = 'https://s3.amazonaws.com/scoir_static/assets/organization_avatar.png';
const OrganizationAvatar = _ref => {
  let {
    className
  } = _ref;
  return /*#__PURE__*/React.createElement("img", {
    className: cn('rc-organization-avatar-img', className),
    src: src
  });
};
OrganizationAvatar.src = src;
OrganizationAvatar.propTypes = {
  className: PropTypes.string
};

const nearestAfter = (arr, val) => {
  return arr.reduce(function (prev, curr) {
    return Math.abs(curr - val) < Math.abs(prev - val) && curr > val ? curr : prev;
  });
};
class TopEventService {
  constructor(options) {
    this.events = {};
    this.topPosition = null;
    this.onTopEventChange = options.onTopEventChange;
    this.resetRegistration();
  }
  resetRegistration() {
    this.positions = [];
    this.registration = {};
  }
  unregister(event) {
    const pos = this.getEventPosition(event.Id);
    remove(this.positions, p => p === parseInt(pos));
    delete this.events[event.Id];
    delete this.registration[pos];
  }
  getEventPosition(id) {
    return findKey(this.registration, event => event.Id === id);
  }
  setScrollRef(ref) {
    this.scrollRef = ref;
    this.registerAll();
    this.addScrollListener();
  }
  addScrollListener() {
    const interval = 500;
    const options = {
      leading: false,
      trailing: true,
      maxWait: interval
    };
    const debounced = method => debounce(method, interval, options);
    this.scrollRef.addEventListener('scroll', debounced(() => this.onScroll()));
    this.scrollRef.addEventListener('resize', debounced(() => this.registerAll()));
  }
  changeTopEvent(newTop) {
    this.topPosition = newTop;
    this.onTopEventChange(this.registration[newTop]);
  }
  onScroll() {
    if (this.positions.length > 0) {
      const newTop = nearestAfter(this.positions, this.scrollRef.scrollTop);
      if (newTop !== this.topPosition) {
        this.changeTopEvent(newTop);
      }
    }
  }
  addEvent(obj) {
    this.events[obj.event.Id] = obj;
  }
  registerAll() {
    this.resetRegistration();
    each(this.events, obj => {
      this.register(obj);
    });
    this.positions.sort((a, b) => a - b);
  }
  register(obj) {
    const position = obj.getPosition();
    this.registration[position] = obj.event;
    this.positions.push(position);
  }
}

const addToGroupArray = (group, key, item) => {
  const found = find(group, current => current.key === key);
  if (found) {
    found.items.push(item);
  } else {
    group.push({
      items: [item],
      key
    });
  }
};
const sort = function (arr) {
  let criteria = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'key';
  return sortBy(arr, [criteria]);
};
const groupByMonths = days => {
  const months = [];
  days.forEach(day => {
    const month = moment(day.key).startOf('month').toISOString();
    addToGroupArray(months, month, day);
  });
  return sort(months);
};
const groupByDays = (events, dateKey) => {
  const days = [];
  events.forEach(event => {
    const day = moment(event[dateKey]).startOf('day').toISOString();
    addToGroupArray(days, day, event);
  });
  return sort(days);
};
var GroupByMonthsDays = {
  getMonths: (events, dateKey) => {
    const sortedByTime = sort(events, dateKey);
    const days = groupByDays(sortedByTime, dateKey);
    const months = groupByMonths(days);
    return months;
  }
};

const StickyRegistrationContext = /*#__PURE__*/React.createContext(null);

class OffsetTopTracker extends React.Component {
  trackerRef = /*#__PURE__*/React.createRef();
  static propTypes = {
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
    exposeOffsetTop: PropTypes.func.isRequired,
    onUnmount: PropTypes.func.isRequired
  };
  componentWillUnmount() {
    this.props.onUnmount();
  }
  componentDidMount() {
    const originalOffset = this.trackerRef.current.offsetTop;
    this.props.exposeOffsetTop(() => {
      return this.trackerRef.current === null ? originalOffset : this.trackerRef.current.offsetTop;
    });
  }
  render() {
    return /*#__PURE__*/React.createElement("div", {
      className: this.props.className,
      ref: this.trackerRef
    }, this.props.children);
  }
}

class StickyItem extends React.Component {
  stickyId = '_' + Math.random().toString(36).substr(2, 9);
  static propTypes = {
    category: PropTypes.string,
    className: PropTypes.string,
    triggerOffset: PropTypes.number,
    children: PropTypes.node.isRequired
  };
  static defaultProps = {
    category: 'default_category',
    triggerOffset: 0
  };
  render() {
    return /*#__PURE__*/React.createElement(OffsetTopTracker, {
      className: this.props.className,
      exposeOffsetTop: getOffsetTopFn => {
        this.getPosition = () => getOffsetTopFn();
      },
      onUnmount: () => this.unregister()
    }, /*#__PURE__*/React.createElement(StickyRegistrationContext.Consumer, null, options => {
      this.unregister = () => options.unregister(this.stickyId);
      options.addSticky({
        id: this.stickyId,
        children: this.props.children,
        category: this.props.category,
        triggerOffset: this.props.triggerOffset,
        getPosition: () => this.getPosition()
      });
      return null;
    }), this.props.children);
  }
}

const nearestBefore = (arr, val) => {
  return arr.reduce(function (prev, curr) {
    return Math.abs(curr - val) < Math.abs(prev - val) && curr < val ? curr : prev;
  });
};
const getCategoryFromObject = (obj, category, defaultVal) => {
  if (obj[category] === undefined) {
    obj[category] = defaultVal;
  }
  return obj[category];
};
class StickyRegistrationService {
  constructor() {
    this.allStickies = {};
    this.reset();
  }
  reset() {
    this.showing = {};
    this.positions = {};
    this.categories = [];
    this.registration = {};
  }
  addSticky(sticky) {
    this.allStickies[sticky.id] = sticky;
  }
  unregister(stickyId) {
    const sticky = this.allStickies[stickyId];
    const position = sticky.getPosition() + sticky.triggerOffset;
    delete this.registration[sticky.category][position];
    delete this.allStickies[stickyId];
    remove(this.positions[sticky.category], n => n === position);
  }
  registerAll() {
    this.reset();
    each(this.allStickies, sticky => {
      this.register(sticky);
    });
    this.categories.forEach(category => {
      this.positions[category].sort((a, b) => a - b);
    });
  }
  register(sticky) {
    const category = sticky.category;
    this.categories = union(this.categories, [category]);
    const registration = getCategoryFromObject(this.registration, category, {});
    const positions = getCategoryFromObject(this.positions, category, []);
    const pos = sticky.getPosition() + sticky.triggerOffset;
    if (registration[pos] === undefined) {
      positions.push(pos);
    }
    registration[pos] = sticky;
  }
  getShowing(scrolled) {
    this.categories.forEach(category => {
      const posCat = this.positions[category];
      const categoryHasStickies = posCat.length > 0 && posCat[0] <= scrolled;
      this.showing[category] = categoryHasStickies ? nearestBefore(posCat, scrolled) : null;
    });
    return this.showing;
  }
  renderStickyCategories() {
    return this.categories.map(category => {
      const showing = this.showing[category];
      const registeredShowing = get(this.registration, `${category}.${showing}`, false);
      if (!registeredShowing || showing === null) {
        return null;
      }
      return /*#__PURE__*/React.createElement("div", {
        key: category,
        className: "sticky"
      }, registeredShowing.children);
    });
  }
  renderStickies() {
    if (this.categories.length > 0) {
      return this.renderStickyCategories();
    }
    return null;
  }
}

class StickyScroller extends React.Component {
  service = new StickyRegistrationService();
  scrollRef = /*#__PURE__*/React.createRef();
  state = {
    showing: {}
  };
  static propTypes = {
    exposeScrollRef: PropTypes.func,
    children: PropTypes.node.isRequired
  };
  registerStickies() {
    this.service.registerAll();
    this.onScroll();
  }
  initialize() {
    this.props.exposeScrollRef(this.scrollRef.current);
    this.addScrollListener();
    this.registerStickies();
  }
  componentDidMount() {
    this.initialize();
  }
  getScrollHeight() {
    return this.scrollRef.current.scrollHeight;
  }
  getSnapshotBeforeUpdate() {
    return {
      scrollHeight: this.getScrollHeight()
    };
  }
  componentDidUpdate(prevProps, prevState, snapshot) {
    const oldScrollHeight = get(snapshot, 'scrollHeight', false);
    if (oldScrollHeight && oldScrollHeight !== this.getScrollHeight()) {
      this.registerStickies();
    }
  }
  addScrollListener() {
    const interval = 50;
    const options = {
      leading: true,
      trailing: true,
      maxWait: interval
    };
    const debounced = method => debounce(method, interval, options);
    this.scrollRef.current.addEventListener('scroll', debounced(() => this.onScroll()));
    this.scrollRef.current.addEventListener('resize', debounced(() => this.registerStickies()));
  }
  getShowing() {
    if (this.scrollRef.current !== null) {
      const scrolled = this.scrollRef.current.scrollTop;
      return this.service.getShowing(scrolled);
    }
    return this.state.showing;
  }
  onScroll() {
    this.setState({
      showing: this.getShowing()
    });
  }
  render() {
    return /*#__PURE__*/React.createElement(ErrorBoundary, null, /*#__PURE__*/React.createElement("div", {
      className: "sticky-scroller"
    }, /*#__PURE__*/React.createElement("div", {
      className: "stickies"
    }, this.service.renderStickies()), /*#__PURE__*/React.createElement("div", {
      className: "scrollable-wrapper",
      ref: this.scrollRef
    }, /*#__PURE__*/React.createElement(StickyRegistrationContext.Provider, {
      className: "context-provider",
      value: {
        unregister: stickyId => {
          this.service.unregister(stickyId);
          const showing = this.getShowing();
          if (!isEqual(showing, this.state.showing)) {
            this.setState({
              showing: showing
            });
          }
        },
        addSticky: sticky => this.service.addSticky(sticky)
      }
    }, this.props.children))));
  }
}

class InfiniteScroll {
  constructor(options) {
    this.scrollRef = options.scrollRef;
    this.topThreshold = options.topThreshold || 65;
    this.bottomThreshold = options.bottomThreshold || 200;
    this.onHitTop = options.onHitTop;
    this.onHitBottom = options.onHitBottom;
    this.reset();
    this.setScrollListener();
  }
  reset() {
    this.isTopEnabled = true;
    this.isBottomEnabled = false;
  }
  setScrollListener() {
    const interval = 1000;
    const options = {
      leading: true,
      trailing: true,
      maxWait: interval
    };
    const debounced = debounce(() => this.onScroll(), interval, options);
    this.scrollRef.addEventListener('scroll', debounced);
  }
  onScroll() {
    const ref = this.scrollRef;
    const scrolled = ref.scrollTop;
    const top = this.topThreshold;
    const bottom = ref.scrollHeight - (ref.offsetHeight + this.bottomThreshold);
    if (!this.isTopEnabled && scrolled > top) {
      this.isTopEnabled = true;
    }
    if (!this.isBottomEnabled && scrolled < bottom) {
      this.isBottomEnabled = true;
    }
    if (this.isTopEnabled && scrolled <= top && isFunction(this.onHitBottom)) {
      this.isTopEnabled = false;
      this.onHitTop();
    } else if (this.isBottomEnabled && scrolled >= bottom && isFunction(this.onHitBottom)) {
      this.isBottomEnabled = false;
      this.onHitBottom();
    }
  }
}

class LoadMoreButton extends React.Component {
  static propTypes = {
    isLoading: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    icon: PropTypes.func.isRequired
  };
  static defaultProps = {
    isLoading: false
  };
  render() {
    return /*#__PURE__*/React.createElement("div", {
      className: "load-more-button-comp"
    }, /*#__PURE__*/React.createElement("div", {
      className: "loading-indicator-container"
    }, this.props.isLoading && /*#__PURE__*/React.createElement(CircularProgress, {
      size: 42,
      thickness: 2
    })), /*#__PURE__*/React.createElement(IconButton, {
      color: "primary",
      "aria-label": "Load more events",
      onClick: () => this.props.onClick(),
      disabled: this.props.isLoading
    }, this.props.icon()));
  }
}

class Event extends React.Component {
  static propTypes = {
    event: PropTypes.object.isRequired,
    eventTile: PropTypes.func.isRequired,
    trackEventPosition: PropTypes.func.isRequired,
    onUnmount: PropTypes.func.isRequired
  };
  render() {
    return /*#__PURE__*/React.createElement(OffsetTopTracker, {
      className: "abstract-agenda-event-container",
      exposeOffsetTop: getOffsetTopFn => {
        this.props.trackEventPosition(() => getOffsetTopFn(), this.props.event);
      },
      onUnmount: () => this.props.onUnmount(this.props.event)
    }, /*#__PURE__*/React.createElement(ErrorBoundary, null, this.props.eventTile(this.props.event)));
  }
}

const dateIsToday = day => moment(day).isSame(moment(), 'day');
class Day extends React.Component {
  static propTypes = {
    getEventKey: PropTypes.func.isRequired,
    day: PropTypes.object.isRequired,
    eventTile: PropTypes.func.isRequired,
    trackEventPosition: PropTypes.func.isRequired,
    onUnmount: PropTypes.func.isRequired
  };
  static defaultProps = {
    eventIdKey: 'Id'
  };
  render() {
    const date = moment(this.props.day.key);
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(StickyItem, {
      className: "abstract-agenda-day",
      category: "day",
      triggerOffset: -50
    }, /*#__PURE__*/React.createElement("div", {
      className: "move-events-up"
    }, /*#__PURE__*/React.createElement("span", {
      className: cn('agenda-label day', {
        today: dateIsToday(this.props.day.key)
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: "day-label-wrapper"
    }, /*#__PURE__*/React.createElement("div", {
      className: "day-number"
    }, moment(this.props.day.key).format('D')), /*#__PURE__*/React.createElement("div", {
      className: "day-name"
    }, date.format('ddd')))))), this.props.day.items.map(event => {
      return /*#__PURE__*/React.createElement(Fragment, {
        key: this.props.getEventKey(event)
      }, /*#__PURE__*/React.createElement(Event, {
        event: event,
        eventTile: event => this.props.eventTile(event),
        trackEventPosition: (getPositionFn, event) => this.props.trackEventPosition(getPositionFn, event),
        onUnmount: event => this.props.onUnmount(event)
      }));
    }));
  }
}

let Month$1 = class Month extends React.Component {
  static propTypes = {
    getEventKey: Day.propTypes.getEventKey,
    month: PropTypes.object.isRequired,
    eventTile: PropTypes.func.isRequired,
    trackEventPosition: PropTypes.func.isRequired,
    onUnmount: PropTypes.func.isRequired,
    monthComponent: PropTypes.string
  };
  static defaultProps = {
    monthComponent: 'div'
  };
  render() {
    const {
      monthComponent: Component
    } = this.props;
    const date = moment(this.props.month.key);
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Component, null, /*#__PURE__*/React.createElement(StickyItem, {
      className: "abstract-agenda-month",
      category: "month",
      triggerOffset: -15
    }, /*#__PURE__*/React.createElement("div", {
      className: "agenda-label month"
    }, /*#__PURE__*/React.createElement("div", {
      className: "border-wrapper"
    }, /*#__PURE__*/React.createElement("span", {
      className: "month-name"
    }, date.format('MMMM')), /*#__PURE__*/React.createElement("span", {
      className: "right-side-label year-number"
    }, date.format('YYYY')))))), this.props.month.items.map(day => {
      return /*#__PURE__*/React.createElement(Fragment, {
        key: `day_${day.key}`
      }, /*#__PURE__*/React.createElement(Day, {
        day: day,
        getEventKey: this.props.getEventKey,
        eventTile: event => this.props.eventTile(event),
        trackEventPosition: (position, event) => this.props.trackEventPosition(position, event),
        onUnmount: event => this.props.onUnmount(event)
      }));
    }));
  }
};

const INITIAL_SCROLL_POSITION = 70;
class Layout extends React.Component {
  state = {
    months: [],
    topEvent: {},
    scrollRef: {}
  };
  static propTypes = {
    isLoading: PropTypes.bool,
    isReloading: PropTypes.bool,
    isEndOfResultsTop: PropTypes.bool,
    isEndOfResultsBottom: PropTypes.bool,
    emptyMessage: PropTypes.string,
    onTopEventChange: PropTypes.func,
    getEventKey: Month$1.propTypes.getEventKey,
    events: PropTypes.array.isRequired,
    eventTile: PropTypes.func.isRequired,
    onLoadBefore: PropTypes.func.isRequired,
    onLoadAfter: PropTypes.func.isRequired,
    monthComponent: PropTypes.any
  };
  static defaultProps = {
    emptyMessage: 'No relevant upcoming events',
    onTopEventChange: noop,
    isLoading: false,
    isReloading: false,
    isEndOfResultsTop: false,
    isEndOfResultsBottom: false
  };
  static getDerivedStateFromProps(nextProps) {
    return {
      months: GroupByMonthsDays.getMonths(nextProps.events, 'StartTime') || []
    };
  }
  getScrollHeight() {
    return get(this.state, 'scrollRef.scrollHeight', 0);
  }
  getSnapshotBeforeUpdate() {
    return {
      scrollHeight: this.getScrollHeight()
    };
  }
  componentDidUpdate(prevProps, prevState, snapshot) {
    const oldScrollHeight = get(snapshot, 'scrollHeight', false);
    if (oldScrollHeight && oldScrollHeight !== this.getScrollHeight()) {
      this.topEventService.registerAll();
    }
    if (this.props.isReloading && !prevProps.isReloading) {
      this.scrollTo(INITIAL_SCROLL_POSITION);
    }
  }
  topEventService = new TopEventService({
    onTopEventChange: event => this.props.onTopEventChange(event)
  });
  renderMonths() {
    if (isEmpty(this.state.months)) {
      return this.renderEmptyState();
    }
    return this.state.months.map(month => {
      return /*#__PURE__*/React.createElement(Fragment, {
        key: `month_${month.key}`
      }, /*#__PURE__*/React.createElement(Month$1, {
        month: month,
        monthComponent: this.props.monthComponent,
        getEventKey: this.props.getEventKey,
        eventTile: event => this.props.eventTile(event),
        trackEventPosition: (getPositionFn, event) => this.trackEventPosition(getPositionFn, event),
        onUnmount: event => this.topEventService.unregister(event)
      }));
    });
  }
  handleHitTop() {
    let ref = this.state.scrollRef;
    const oldHeight = ref.scrollHeight;
    this.props.onLoadBefore().then(() => {
      this.scrollTo(ref.scrollHeight - oldHeight);
    });
  }
  handleHitBottom() {
    this.props.onLoadAfter();
  }
  setInfiniteScroll() {
    this.infiniteScroll = new InfiniteScroll({
      scrollRef: this.state.scrollRef,
      onHitTop: () => this.handleHitTop(),
      onHitBottom: () => this.handleHitBottom()
    });
  }
  scrollTo(position) {
    const ref = this.state.scrollRef;
    ref.scrollTop = position;
  }
  handleScrollRef(ref) {
    this.setState({
      scrollRef: ref
    }, () => {
      this.setScrollRef();
      this.setInfiniteScroll();
      this.scrollTo(INITIAL_SCROLL_POSITION);
    });
  }
  setScrollRef() {
    this.topEventService.setScrollRef(this.state.scrollRef);
  }
  trackEventPosition(getPositionFn, event) {
    this.topEventService.addEvent({
      getPosition: () => getPositionFn(),
      event: event
    });
  }
  renderEmptyState() {
    return /*#__PURE__*/React.createElement("div", {
      className: "empty-state"
    }, this.props.isLoading ? /*#__PURE__*/React.createElement(CircularProgress, {
      size: 44,
      thickness: 2
    }) : this.props.emptyMessage);
  }
  renderResultEnd(end) {
    return /*#__PURE__*/React.createElement("div", {
      className: `end-of-results ${end}`
    }, "You have reached the ", end, " of this list.");
  }
  renderLoadMore(onClick, icon) {
    return /*#__PURE__*/React.createElement(LoadMoreButton, {
      isLoading: this.props.isLoading,
      onClick: () => this[onClick](),
      icon: () => /*#__PURE__*/React.createElement(FontAwesomeIcon, {
        size: "xs",
        icon: icon
      })
    });
  }
  render() {
    const empty = isEmpty(this.state.months) && this.props.isEndOfResultsTop && this.props.isEndOfResultsBottom;
    return /*#__PURE__*/React.createElement("div", {
      className: "abstract-agenda-layout"
    }, empty ? this.renderEmptyState() : /*#__PURE__*/React.createElement(StickyScroller, {
      exposeScrollRef: ref => this.handleScrollRef(ref)
    }, this.props.isEndOfResultsTop ? this.renderResultEnd('top') : this.renderLoadMore('handleHitTop', faChevronUp), this.renderMonths(), this.props.isEndOfResultsBottom ? this.renderResultEnd('bottom') : this.renderLoadMore('handleHitBottom', faChevronDown)));
  }
}

class Tile extends React.Component {
  static propTypes = {
    tabColor: PropTypes.string,
    leftPanel: PropTypes.node.isRequired,
    middlePanel: PropTypes.node,
    rightPanel: PropTypes.node,
    onClick: PropTypes.func
  };
  static defaultProps = {
    tabColor: '#8fbd41'
  };
  state = {
    isViewMoreModalOpen: false
  };
  render() {
    const {
      leftPanel,
      middlePanel,
      onClick,
      rightPanel,
      tabColor
    } = this.props;
    return /*#__PURE__*/React.createElement("div", {
      className: cn('abstract-event-tile-comp', {
        'hoverable-tile': !isUndefined(onClick)
      }),
      onClick: onClick
    }, /*#__PURE__*/React.createElement("div", {
      className: "left-container",
      style: {
        borderLeftColor: tabColor
      }
    }, leftPanel), /*#__PURE__*/React.createElement("div", {
      className: "middle-right-wrapper",
      style: {
        borderLeftColor: tabColor
      }
    }, middlePanel && /*#__PURE__*/React.createElement("div", {
      className: "middle-container"
    }, middlePanel), rightPanel && /*#__PURE__*/React.createElement("div", {
      className: "right-container"
    }, rightPanel)));
  }
}

const DisplayParticipant = _ref => {
  let {
    participant,
    renderCollegeLink
  } = _ref;
  const {
    City,
    Scid,
    State,
    CollegeName,
    OrganizationName
  } = participant;
  const hasScid = !isUndefined(Scid);
  const hasCity = !isEmpty(City);
  const hasState = !isEmpty(State);
  const label = CollegeName || OrganizationName;
  return /*#__PURE__*/React.createElement("div", {
    className: "abstract-event-tile-participant-name-comp"
  }, /*#__PURE__*/React.createElement("div", {
    className: "participant-logo-wrapper"
  }, hasScid ? /*#__PURE__*/React.createElement(CollegeCrest, {
    scid: Scid,
    size: "sm"
  }) : /*#__PURE__*/React.createElement(OrganizationAvatar, {
    className: "participant-logo"
  })), /*#__PURE__*/React.createElement("div", {
    className: "participant-info"
  }, /*#__PURE__*/React.createElement("div", {
    className: "participant-name"
  }, hasScid && !isUndefined(renderCollegeLink) ? renderCollegeLink({
    scid: Scid,
    label
  }) : label), (hasCity || hasState) && /*#__PURE__*/React.createElement("div", {
    className: "participant-location"
  }, City, hasCity && hasState && ', ', State)));
};
DisplayParticipant.propTypes = {
  participant: PropTypes.object.isRequired,
  renderCollegeLink: PropTypes.func
};

const DATE_FORMAT = 'MMM Do';
class AllDayEventDuration extends React.Component {
  isMultiDay(start, end) {
    return end.diff(start, 'days') > 1;
  }
  renderMultiDay(start, end) {
    const startLabel = start.format(DATE_FORMAT);
    const endLabel = end.subtract(1, 'day').format(DATE_FORMAT);
    return /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("span", {
      className: "time-label"
    }, startLabel), /*#__PURE__*/React.createElement("span", null, " \u2013 "), /*#__PURE__*/React.createElement("span", {
      className: "time-label"
    }, endLabel));
  }
  renderSingleDay(start) {
    const startLabel = start.format(DATE_FORMAT);
    return /*#__PURE__*/React.createElement("span", {
      className: "time-label"
    }, startLabel);
  }
  render() {
    const start = moment.utc(this.props.event.StartTime);
    const end = moment.utc(this.props.event.EndTime);
    const isMultiDay = this.isMultiDay(start, end);
    return /*#__PURE__*/React.createElement("span", {
      className: "all-day-event-duration-comp"
    }, isMultiDay && this.renderMultiDay(start, end), !isMultiDay && this.renderSingleDay(start));
  }
}
AllDayEventDuration.propTypes = {
  event: PropTypes.object.isRequired
};

let EventDuration$1 = class EventDuration extends React.Component {
  renderTimeSpan() {
    const sameDayformat = 'hh:mm A';
    const multiDayformat = 'MMM Do hh:mm A';
    const start = moment(this.props.event.StartTime);
    const end = moment(this.props.event.EndTime);
    const isSameDay = start.isSame(end, 'day');
    let startLabel = start.format(sameDayformat);
    let endLabel = end.format(sameDayformat);
    if (!isSameDay) {
      startLabel = start.format(multiDayformat);
      endLabel = end.format(multiDayformat);
    }
    return /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("span", {
      className: "time-label"
    }, startLabel), /*#__PURE__*/React.createElement("span", null, " \u2013 "), /*#__PURE__*/React.createElement("span", {
      className: "time-label"
    }, endLabel));
  }
  render() {
    return /*#__PURE__*/React.createElement("span", {
      className: "abstract-agenda-event-duration-comp"
    }, this.props.event.AllDay && /*#__PURE__*/React.createElement(AllDayEventDuration, {
      event: this.props.event
    }), !this.props.event.AllDay && this.renderTimeSpan());
  }
};
EventDuration$1.propTypes = {
  event: PropTypes.object.isRequired
};

const Location = _ref => {
  let {
    isVirtual = false,
    place,
    launchAs: LaunchAs
  } = _ref;
  if (!place) {
    return 'TBD';
  }
  if (isVirtual) {
    return /*#__PURE__*/React.createElement(LaunchAs, {
      href: place,
      target: "_blank",
      rel: "noopener noreferrer"
    }, "Launch Meeting\xA0", /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      icon: faVideoCamera
    }));
  }
  return place;
};
Location.propTypes = {
  place: PropTypes.string,
  isVirtual: PropTypes.bool,
  launchAs: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
};

const tooltipText = 'Virtual meeting links are only available to users registered for the event';
const Tooltipped = _ref => {
  let {
    children
  } = _ref;
  return /*#__PURE__*/React.createElement(Tooltip, {
    title: tooltipText
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("span", {
    className: "mock-launch-link"
  }, children), /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, ". ", tooltipText)));
};
Tooltipped.propTypes = {
  children: PropTypes.any.isRequired
};
class VisitAttendeeLocation extends React.Component {
  static propTypes = {
    showAttendeeCount: PropTypes.bool,
    attendeeCount: PropTypes.number,
    event: PropTypes.object.isRequired,
    canLaunchVirtualMeeting: PropTypes.bool
  };
  static defaultProps = {
    showAttendeeCount: true
  };
  render() {
    const {
      attendeeCount,
      event,
      showAttendeeCount,
      canLaunchVirtualMeeting
    } = this.props;
    return /*#__PURE__*/React.createElement("div", {
      className: "visit-attendee-location-comp"
    }, /*#__PURE__*/React.createElement("div", {
      className: "event-time-location-container"
    }, /*#__PURE__*/React.createElement("div", {
      className: "event-label"
    }, "Time:"), /*#__PURE__*/React.createElement("div", {
      className: "event-duration"
    }, /*#__PURE__*/React.createElement(EventDuration$1, {
      event: event
    })), /*#__PURE__*/React.createElement("div", {
      className: "event-location-wrapper"
    }, /*#__PURE__*/React.createElement("div", {
      className: "event-label"
    }, event.Virtual ? 'Meeting Link' : 'Location', ":"), /*#__PURE__*/React.createElement("div", {
      className: "event-location"
    }, /*#__PURE__*/React.createElement(Location, {
      launchAs: canLaunchVirtualMeeting ? 'a' : Tooltipped,
      isVirtual: event.Virtual,
      place: get(event, 'Location.PlaceName')
    })))), showAttendeeCount && /*#__PURE__*/React.createElement("div", {
      className: "attendees-container"
    }, /*#__PURE__*/React.createElement("div", {
      className: "student-icon-wrapper"
    }, /*#__PURE__*/React.createElement("span", {
      className: "registered-count"
    }, isUndefined(attendeeCount) ? size(event.Attendees) : attendeeCount), /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      size: "2x",
      icon: faUser
    })), /*#__PURE__*/React.createElement("div", {
      className: "registered-label"
    }, "Registered")));
  }
}

class ViewMoreParticipantsModal extends React.Component {
  static propTypes = {
    open: PropTypes.bool,
    participants: PropTypes.array,
    onClose: PropTypes.func.isRequired
  };
  static defaultProps = {
    open: false,
    participants: []
  };
  render() {
    return /*#__PURE__*/React.createElement(DismissibleDialog, {
      className: "view-more-participants-modal-comp",
      open: this.props.open,
      onClose: this.props.onClose,
      headerText: "Attending Schools"
    }, /*#__PURE__*/React.createElement(DismissibleDialog.Body, null, /*#__PURE__*/React.createElement(Box, {
      display: "flex",
      flexDirection: "column",
      gridGap: 12
    }, this.props.participants.map(participant => {
      const key = participant.ContactId;
      return /*#__PURE__*/React.createElement("div", {
        key: key
      }, /*#__PURE__*/React.createElement(DisplayParticipant, {
        participant: participant
      }));
    }))), /*#__PURE__*/React.createElement(DismissibleDialog.Footer, null, /*#__PURE__*/React.createElement(DismissibleDialog.Footer.CloseButton, {
      variant: "contained"
    }, "Close")));
  }
}

var index = {
  Layout,
  Tile,
  DisplayParticipant,
  ViewMoreParticipantsModal,
  VisitAttendeeLocation
};

class EventDuration extends React.Component {
  isMultiDay(start, end) {
    return end.diff(start, 'days') > 1;
  }
  renderAllDay() {
    const format = 'ddd MMM Do, YYYY';
    const start = moment$1.utc(this.props.event.StartTime);
    const startLabel = start.format(format);
    const end = moment$1.utc(this.props.event.EndTime);
    if (this.isMultiDay(start, end)) {
      const endLabel = end.subtract(1, 'day').format(format);
      return /*#__PURE__*/React.createElement("span", null, startLabel, " \u2013 ", endLabel);
    }
    return /*#__PURE__*/React.createElement("span", null, startLabel);
  }
  timeZoneEventDuration() {
    const {
      event
    } = this.props;
    const format = 'ddd MMM Do, YYYY | hh:mmA';
    const timeZone = moment.tz(event.StartTime, event.StartTimeZone).format('z');
    const start = moment.tz(event.StartTime, event.StartTimeZone);
    const end = moment.tz(event.EndTime, event.StartTimeZone);
    const isSameDay = start.isSame(end, 'day');
    let startLabel = start.format('hh:mmA');
    let endLabel = end.format('hh:mmA');
    if (!isSameDay) {
      endLabel = end.format(format);
    }
    return /*#__PURE__*/React.createElement("span", null, "Local school time ", startLabel, " \u2013 ", endLabel, " ", timeZone);
  }
  renderTimeSpan() {
    const {
      event,
      isDifferentTimeZone
    } = this.props;
    const format = 'ddd MMM Do, YYYY | hh:mmA';
    const start = moment$1(event.StartTime);
    const end = moment$1(event.EndTime);
    const isSameDay = start.isSame(end, 'day');
    const startLabel = start.format(format);
    let endLabel = end.format('hh:mmA');
    if (!isSameDay) {
      endLabel = end.format(format);
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "rc-event-duration-comp"
    }, /*#__PURE__*/React.createElement("span", null, startLabel, " \u2013 ", endLabel, "\xA0", this.props.showLocalTimeZone && /*#__PURE__*/React.createElement("span", {
      className: "rc-local-timezone-display"
    }, moment.tz(event.StartTime, moment.tz.guess()).format('z'))), isDifferentTimeZone && /*#__PURE__*/React.createElement("div", {
      className: "rc-local-school-time"
    }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      size: "1rem",
      icon: faClock
    }), this.timeZoneEventDuration()));
  }
  render() {
    if (this.props.event.AllDay) {
      return this.renderAllDay();
    }
    return this.renderTimeSpan();
  }
}
EventDuration.propTypes = {
  event: PropTypes.object.isRequired,
  isDifferentTimeZone: PropTypes.bool,
  showLocalTimeZone: PropTypes.bool
};
EventDuration.defaultProps = {
  showLocalTimeZone: false
};

const EventDetailsTitle = _ref => {
  let {
    children,
    className
  } = _ref;
  return /*#__PURE__*/React.createElement("div", {
    className: cn('event-details-title-comp', className)
  }, children);
};
EventDetailsTitle.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
};

class EventDetailsSection extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement("div", {
      className: cn('event-details-section-comp', this.props.className)
    }, this.props.heading && /*#__PURE__*/React.createElement("span", {
      className: "item-heading"
    }, this.props.heading), /*#__PURE__*/React.createElement("div", {
      className: cn('section-content', {
        'has-defined-height': !isUndefined(this.props.maximumHeight)
      }),
      style: {
        maxHeight: this.props.maximumHeight
      }
    }, this.props.children));
  }
}
EventDetailsSection.propTypes = {
  heading: PropTypes.node,
  maximumHeight: PropTypes.number,
  children: PropTypes.node.isRequired,
  className: PropTypes.string
};

const getDayName = day => {
  const cap = day.charAt(0).toUpperCase();
  const restOfName = day.slice(1);
  return `${cap}${restOfName}`;
};
let CalendarHeader$1 = class CalendarHeader extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement("div", {
      className: "calendar-header-month-comp"
    }, /*#__PURE__*/React.createElement("div", {
      className: "calendar-header-row"
    }, moment.weekdays().map(day => /*#__PURE__*/React.createElement("div", {
      className: "day-of-week-header",
      key: day
    }, getDayName(day)))));
  }
};

const getFormattedMoment = timeStamp => {
  return moment(timeStamp).format('YYYYMMDD');
};
const getMultidayEnd = event => {
  const endTime = moment(event.EndTime);
  return event.EndTimeZone ? moment(getFormattedMoment(moment(endTime).tz(event.EndTimeZone)), 'YYYYMMDD') : moment(getFormattedMoment(endTime), 'YYYYMMDD');
};
const getMultiDayStart = event => {
  const startTime = moment(event.StartTime);
  return event.StartTimeZone ? moment(moment(startTime).tz(event.StartTimeZone).format('YYYYMMDD'), 'YYYYMMDD') : moment(getFormattedMoment(startTime), 'YYYYMMDD');
};
const getIsMultiDay = event => {
  return !moment(event.StartTime).isSame(event.EndTime, 'day');
};
const isAllDayForDate = (targetDate, event) => {
  const isMultiDay = getIsMultiDay(event);
  if (!event.AllDay && !isMultiDay) {
    return false;
  }
  const allDayStart = event.AllDay ? moment(moment.utc(event.StartTime).format('YYYYMMDD'), 'YYYYMMDD') : getMultiDayStart(event);
  const allDayEnd = event.AllDay ? moment(moment.utc(event.EndTime).add(-1, 'day').format('YYYYMMDD'), 'YYYYMMDD') : getMultidayEnd(event);
  return allDayStart.isSameOrBefore(targetDate.clone().startOf('day'), 'day') && allDayEnd.isSameOrAfter(targetDate.clone().endOf('day'), 'day');
};

const idDiff = (a, b) => {
  if (a.Id < b.Id) {
    return -1;
  }
  return 1;
};
var eventSorter = ((a, b) => {
  const eventA = moment$1(a.StartTime);
  const eventB = moment$1(b.StartTime);
  if (a.AllDay && !b.AllDay) {
    return -1;
  }
  if (b.AllDay && !a.AllDay) {
    return 1;
  }
  if (eventA.isSame(eventB, 'minute')) {
    return idDiff(a, b);
  }
  return eventA.diff(eventB);
});

const isClickable = props => {
  const thisDay = moment(props.day);
  const {
    config
  } = props.configFn(thisDay);
  return props.onTimeSelect !== undefined && config.canCreate;
};
class EventDay extends React.Component {
  selectDate() {
    this.props.onTimeSelect(moment(this.props.day).startOf('day').toISOString());
  }
  render() {
    const {
      day,
      events,
      isCurrentMonth,
      isToday,
      connectDropTarget,
      isHovered,
      eventDayComponent,
      configFn
    } = this.props;
    const {
      theme
    } = configFn(day);
    const canClick = isClickable(this.props);
    return connectDropTarget( /*#__PURE__*/React.createElement("div", {
      className: cn('event-day-comp', {
        'in-current-month': isCurrentMonth,
        today: isToday,
        dragHover: isHovered
      }),
      style: {
        background: get(theme, 'day.background')
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: cn('day-of-month-header', {
        clickable: canClick
      }),
      onClick: canClick ? () => this.selectDate() : noop
    }, day.format('D')), /*#__PURE__*/React.createElement("div", {
      className: "events-in-day"
    }, eventDayComponent({
      events,
      renderDate: day
    }))));
  }
}
EventDay.propTypes = {
  events: PropTypes.array.isRequired,
  onTimeSelect: PropTypes.func,
  day: PropTypes.object.isRequired,
  isCurrentMonth: PropTypes.bool.isRequired,
  isToday: PropTypes.bool.isRequired,
  eventDayComponent: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  isHovered: PropTypes.bool.isRequired,
  configFn: PropTypes.func.isRequired
};
const spec$1 = {
  drop: props => {
    return {
      type: 'date',
      date: props.day
    };
  },
  canDrop: props => isClickable(props)
};
const collect$1 = (connect, monitor) => {
  return {
    connectDropTarget: connect.dropTarget(),
    isHovered: monitor.canDrop() && monitor.isOver()
  };
};
const DropTargetEventDay = DropTarget(abstractEventType, spec$1, collect$1)(EventDay);
var EventDay$1 = withConfigForTime(DropTargetEventDay);

class EventsMonthView extends React.Component {
  getEventsFor(targetDay) {
    const events = filter(this.props.events, event => {
      return event.AllDay ? isAllDayForDate(targetDay, event) : moment(event.StartTime).isSameOrBefore(targetDay.clone().startOf('day'), 'day') && moment(event.EndTime).isSameOrAfter(targetDay.clone().endOf('day'), 'day');
    });
    events.sort(eventSorter);
    !isUndefined(this.props.secondarySortFn) && events.sort(this.props.secondarySortFn);
    return events;
  }
  render() {
    const today = moment();
    const startOfMonthView = this.props.focusDate.clone().startOf('month').startOf('week');
    const nextMonth = this.props.focusDate.clone().startOf('month').add(1, 'month');
    const numberOfWeeks = Math.ceil(nextMonth.diff(startOfMonthView, 'days') / 7);
    return /*#__PURE__*/React.createElement("tbody", {
      className: "events-month-view-comp"
    }, times(numberOfWeeks).map(weekIdx => {
      return /*#__PURE__*/React.createElement("tr", {
        key: weekIdx,
        className: "week"
      }, [0, 1, 2, 3, 4, 5, 6].map(dayIdx => {
        const thisDay = startOfMonthView.clone();
        startOfMonthView.add(1, 'day');
        return /*#__PURE__*/React.createElement("td", {
          key: dayIdx,
          className: "day"
        }, /*#__PURE__*/React.createElement(EventDay$1, {
          day: thisDay,
          onTimeSelect: this.props.onTimeSelect,
          events: this.getEventsFor(thisDay),
          eventDayComponent: this.props.eventDayComponent,
          isToday: today.isSame(thisDay, 'day'),
          isCurrentMonth: thisDay.isSame(this.props.focusDate, 'month')
        }));
      }));
    }));
  }
}
EventsMonthView.propTypes = {
  onTimeSelect: PropTypes.func,
  events: PropTypes.array.isRequired,
  focusDate: PropTypes.object.isRequired,
  eventDayComponent: PropTypes.func.isRequired,
  secondarySortFn: PropTypes.func
};

class MonthView extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CalendarHeader$1, null), /*#__PURE__*/React.createElement("div", {
      className: "month-view"
    }, /*#__PURE__*/React.createElement("table", {
      className: "calendar"
    }, /*#__PURE__*/React.createElement(EventsMonthView, {
      events: this.props.events,
      onTimeSelect: this.props.onTimeSelect,
      focusDate: this.props.focusDate,
      secondarySortFn: this.props.secondarySortFn,
      eventDayComponent: this.props.eventDayComponent
    }))));
  }
}
MonthView.propTypes = {
  onTimeSelect: PropTypes.func,
  events: PropTypes.array.isRequired,
  focusDate: PropTypes.object.isRequired,
  eventDayComponent: PropTypes.func.isRequired,
  secondarySortFn: PropTypes.func
};

class AbstractFocusControl extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement("div", {
      className: cn('abstract-focus-control-comp', this.props.className)
    }, /*#__PURE__*/React.createElement(IconButton, {
      size: "small",
      onClick: this.props.onGoPrior,
      "aria-label": "go prior"
    }, this.props.priorElem), /*#__PURE__*/React.createElement(Box, {
      textAlign: "center"
    }, this.props.children), /*#__PURE__*/React.createElement(IconButton, {
      size: "small",
      onClick: this.props.onGoAhead,
      "aria-label": "go ahead"
    }, this.props.aheadElem));
  }
}
AbstractFocusControl.propTypes = {
  className: PropTypes.string,
  priorElem: PropTypes.node.isRequired,
  aheadElem: PropTypes.node.isRequired,
  onGoPrior: PropTypes.func.isRequired,
  onGoAhead: PropTypes.func.isRequired,
  children: PropTypes.node
};

let FocusControl$1 = class FocusControl extends React.Component {
  oneMonthPrior() {
    const newFocus = moment(this.props.focusDate).subtract(1, 'month');
    this.props.onFocusDateChange(newFocus);
  }
  oneMonthAhead() {
    const newFocus = moment(this.props.focusDate).add(1, 'month');
    this.props.onFocusDateChange(newFocus);
  }
  render() {
    return /*#__PURE__*/React.createElement(AbstractFocusControl, {
      className: "month-focus-control-comp",
      priorElem: this.props.priorElem,
      aheadElem: this.props.aheadElem,
      onGoPrior: () => this.oneMonthPrior(),
      onGoAhead: () => this.oneMonthAhead()
    }, /*#__PURE__*/React.createElement("span", {
      className: "month-name"
    }, this.props.focusDate.format('MMMM')));
  }
};
FocusControl$1.propTypes = {
  focusDate: PropTypes.object.isRequired,
  priorElem: PropTypes.node.isRequired,
  aheadElem: PropTypes.node.isRequired,
  onFocusDateChange: PropTypes.func.isRequired
};

const ContainerContext = /*#__PURE__*/React.createContext('ContainerContext');

const themeToStyle = theme => ({
  background: get(theme, 'day.background')
});
class CalendarHeader extends React.Component {
  createHeader(date) {
    const dayOfWeek = date.format('ddd');
    const classNames = cn('day-header', {
      today: moment().isSame(date, 'day')
    });
    const {
      theme
    } = this.props.configFn(date);
    const cap = dayOfWeek.charAt(0).toUpperCase();
    const stringWithoutFirstLetter = dayOfWeek.slice(1);
    const dayName = `${cap}${stringWithoutFirstLetter}`;
    return /*#__PURE__*/React.createElement("div", {
      className: classNames,
      key: dayOfWeek,
      style: themeToStyle(theme)
    }, /*#__PURE__*/React.createElement("strong", null, dayName), /*#__PURE__*/React.createElement("span", null, date.format('MM/DD')));
  }
  mapEachDayThisWeek(cb) {
    const focus = moment(this.props.focusDate);
    const days = [0, 1, 2, 3, 4, 5, 6];
    return days.map(i => {
      const adjustedFocus = focus.clone().day(i);
      return cb(adjustedFocus);
    });
  }
  addAllDayEvents(day) {
    const allDayEvents = filter(this.props.events, event => isAllDayForDate(day, event));
    allDayEvents.sort(eventSorter);
    !isUndefined(this.props.secondarySortFn) && allDayEvents.sort(this.props.secondarySortFn);
    const EventComp = this.props.eventComponent;
    const {
      theme
    } = this.props.configFn(day);
    return /*#__PURE__*/React.createElement("div", {
      className: "events",
      key: day.format(),
      style: themeToStyle(theme)
    }, /*#__PURE__*/React.createElement(ContainerContext.Provider, {
      value: {
        container: document.body
      }
    }, allDayEvents.map(event => {
      return /*#__PURE__*/React.createElement("div", {
        className: "event-wrapper",
        key: event.Id
      }, /*#__PURE__*/React.createElement(EventComp, {
        container: document.body,
        className: "ch-event-comp",
        event: event,
        renderDate: day
      }));
    })));
  }
  render() {
    return /*#__PURE__*/React.createElement("div", {
      className: "calendar-header-week-comp"
    }, /*#__PURE__*/React.createElement("div", {
      className: "header-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "week-header-date-info"
    }, /*#__PURE__*/React.createElement("div", {
      className: "time-zone"
    }, /*#__PURE__*/React.createElement("span", null, moment.tz(moment.tz.guess()).format('z'))), this.mapEachDayThisWeek(day => {
      return this.createHeader(day);
    })), /*#__PURE__*/React.createElement("div", {
      className: "all-day-events-wrapper"
    }, /*#__PURE__*/React.createElement("div", {
      className: "all-day-label"
    }, /*#__PURE__*/React.createElement("span", null, "all-day")), /*#__PURE__*/React.createElement("div", {
      className: "all-day-events"
    }, this.mapEachDayThisWeek(day => {
      return this.addAllDayEvents(day);
    })))), /*#__PURE__*/React.createElement("div", {
      className: "scroll-dummy"
    }));
  }
}
CalendarHeader.propTypes = {
  focusDate: PropTypes.object.isRequired,
  events: PropTypes.array.isRequired,
  eventComponent: PropTypes.func.isRequired,
  secondarySortFn: PropTypes.func,
  configFn: PropTypes.func.isRequired
};
var WeekCalendarHeader = withConfigForTime(CalendarHeader);

class TimeLineLabels extends React.Component {
  render() {
    const today = moment();
    const HOURS = 24;
    const timeLineLabels = [];
    let count;
    for (count = 1; count < HOURS; count++) {
      today.hour(count);
      timeLineLabels.push( /*#__PURE__*/React.createElement("div", {
        className: "hour-label",
        key: count
      }, /*#__PURE__*/React.createElement("span", null, today.format('h A'))));
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "time-line-labels-comp"
    }, timeLineLabels);
  }
}

class TimeChunk extends React.Component {
  static propTypes = {
    chunkId: PropTypes.number.isRequired,
    isHour: PropTypes.bool.isRequired,
    isHalfHour: PropTypes.bool.isRequired,
    canCreate: PropTypes.bool.isRequired,
    onCreate: PropTypes.func,
    asMoment: PropTypes.func.isRequired,
    connectDropTarget: PropTypes.func.isRequired,
    isHovered: PropTypes.bool.isRequired
  };
  static defaultProps = {
    isHour: false,
    isHalfHour: false,
    canCreate: false,
    isHovered: false
  };
  render() {
    const {
      connectDropTarget
    } = this.props;
    return connectDropTarget( /*#__PURE__*/React.createElement("div", {
      className: cn('time-chunk-comp', {
        hour: this.props.isHour,
        'half-hour': this.props.isHalfHour,
        clickable: this.props.canCreate,
        dragHover: this.props.isHovered
      }),
      role: this.props.canCreate ? 'button' : 'presentation',
      onClick: () => this.props.onCreate && this.props.onCreate(this.props.chunkId),
      "data-label": `+ New at ${this.props.asMoment(this.props.chunkId).format('LT')}`
    }));
  }
}
const spec = {
  drop: props => {
    return {
      type: 'datetime',
      startTime: props.asMoment(props.chunkId),
      minuteIncrement: 15
    };
  },
  canDrop: props => props.canCreate
};
const collect = (connect, monitor) => {
  return {
    connectDropTarget: connect.dropTarget(),
    isHovered: monitor.canDrop() && monitor.isOver()
  };
};
const DropTargetTimeChunk = DropTarget(abstractEventType, spec, collect)(TimeChunk);
var TimeChunk$1 = /*#__PURE__*/React.memo(DropTargetTimeChunk);

const TIME_CHUNKS = 96;
const Timeline = props => {
  const {
    onCreate,
    asMoment
  } = props;
  return times(TIME_CHUNKS, idx => /*#__PURE__*/React.createElement(TimeChunk$1, {
    key: idx,
    chunkId: idx,
    isHour: idx > 0 && (idx + 1) % 4 === 0,
    isHalfHour: idx > 0 && (idx + 1) % 2 === 0,
    canCreate: !!onCreate,
    onCreate: onCreate,
    asMoment: asMoment
  }));
};
const MemoedTimeline = /*#__PURE__*/React.memo(Timeline);
const DayOfWeek = _ref => {
  let {
    style,
    dayIdx,
    asStartOfDay,
    onTimelineSelect,
    children
  } = _ref;
  const asMoment = useCallback(chunkId => {
    return asStartOfDay(dayIdx).add(chunkId * 15, 'minutes');
  }, [dayIdx, asStartOfDay]);
  const onCreate = useCallback(chunkId => {
    const start = asMoment(chunkId);
    const end = asMoment(chunkId + 4);
    onTimelineSelect(start.toISOString(), end.toISOString());
  }, [asMoment, onTimelineSelect]);
  return /*#__PURE__*/React.createElement("div", {
    className: "day-of-week",
    style: style
  }, /*#__PURE__*/React.createElement("div", {
    className: "time-line-wrapper"
  }, /*#__PURE__*/React.createElement(MemoedTimeline, {
    onCreate: onTimelineSelect ? onCreate : undefined,
    asMoment: asMoment
  }), children));
};
DayOfWeek.propTypes = {
  style: PropTypes.object,
  dayIdx: PropTypes.number.isRequired,
  asStartOfDay: PropTypes.func.isRequired,
  onTimelineSelect: PropTypes.func,
  children: PropTypes.any
};

var intersectionCalculator = (events => {
  const intersections = {};
  const boundedEvents = filter(events, event => {
    return !event.AllDay && !getIsMultiDay(event);
  });
  const addIntersection = (event, intersectedEvent) => {
    const eventIntersections = intersections[event.Id];
    const entry = {
      event: intersectedEvent
    };
    isArray(eventIntersections) ? eventIntersections.push(entry) : intersections[event.Id] = [entry];
  };
  each(boundedEvents, (event, i) => {
    const eventStart = moment(event.StartTime);
    const eventEnd = moment(event.EndTime);
    for (let j = i + 1; j < boundedEvents.length; j++) {
      let checkEvent = boundedEvents[j];
      let checkStart = moment(checkEvent.StartTime);
      let checkEnd = moment(checkEvent.EndTime);
      if (eventStart.isBetween(checkStart, checkEnd, 'minute', '[)') || checkStart.isBetween(eventStart, eventEnd, 'minute', '[)')) {
        addIntersection(event, checkEvent);
        addIntersection(checkEvent, event);
      }
    }
  });
  return intersections;
});

const bgStyleTheme = memoize(background => ({
  background
}));
class EventsWeekView extends React.Component {
  state = {
    intersections: intersectionCalculator(this.props.events)
  };
  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.events, this.props.events)) {
      const intersections = intersectionCalculator(this.props.events);
      this.setState({
        intersections
      });
    }
  }
  createEvents(day, events) {
    const startOfThisDay = day.clone().startOf('day');
    const endOfThisDay = day.clone().endOf('day');
    const QUARTER_HOUR_HEIGHT = 12.5;
    const intersections = cloneDeep(this.state.intersections);
    const getPositionV = event => {
      const eventDate = moment.max(moment(event.StartTime), startOfThisDay);
      const quarter = Math.floor(eventDate.minute() / 15);
      const totalQuarters = eventDate.hour() * 4 + quarter;
      return `${totalQuarters * QUARTER_HOUR_HEIGHT}px`;
    };
    const getWidth = event => {
      const eventIntersections = intersections[event.Id];
      if (eventIntersections && eventIntersections.length > 0) {
        return `${100 / (eventIntersections.length + 1)}%`;
      }
      return '100%';
    };
    const getPositionH = event => {
      const eventIntersections = intersections[event.Id];
      if (eventIntersections && eventIntersections.length > 0) {
        const printedEvents = filter(eventIntersections, entry => {
          return entry.isPrinted;
        });
        each(eventIntersections, entry => {
          const eventRef = find(intersections[entry.event.Id], entry => {
            return entry.event.Id === event.Id;
          });
          eventRef.isPrinted = true;
        });
        if (printedEvents.length > 0) {
          return `${100 * (printedEvents.length / (eventIntersections.length + 1))}%`;
        }
      }
      return '0';
    };
    const getHeight = event => {
      const start = moment.max(moment(event.StartTime), startOfThisDay);
      const endOfEvent = moment.min(moment(event.EndTime), endOfThisDay);
      const quarters = Math.ceil(endOfEvent.diff(start, 'minutes', true) / 15);
      return `${quarters * QUARTER_HOUR_HEIGHT}px`;
    };
    events.sort(eventSorter);
    return events.map(event => {
      const positionV = getPositionV(event);
      const positionH = getPositionH(event);
      const width = getWidth(event);
      const height = getHeight(event);
      const style = {
        top: positionV,
        left: positionH,
        width: width,
        height: height
      };
      return /*#__PURE__*/React.createElement("div", {
        className: "event-wrapper",
        key: event.Id,
        style: style
      }, this.props.eventComponent({
        event,
        renderDate: startOfThisDay
      }));
    });
  }
  mapEachDayThisWeek(cb) {
    const focus = moment(this.props.focusDate);
    const days = [0, 1, 2, 3, 4, 5, 6];
    return days.map(i => {
      const adjustedFocus = focus.day(i);
      return cb(adjustedFocus, i);
    });
  }
  asStartOfDay = dayIdx => {
    return moment(this.props.focusDate).startOf('day').day(dayIdx);
  };
  render() {
    return /*#__PURE__*/React.createElement("div", {
      className: "events-week-view-comp"
    }, this.mapEachDayThisWeek((day, idx) => {
      const startOfDay = day.clone().startOf('day');
      const endOfDay = day.clone().endOf('day');
      const events = filter(this.props.events, event => {
        const mmntStart = moment(event.StartTime);
        const mmntEnd = moment(event.EndTime);
        return !event.AllDay && mmntStart.isSame(mmntEnd, 'day') && mmntStart.isSameOrBefore(startOfDay, 'day') && mmntEnd.isSameOrAfter(endOfDay, 'day');
      });
      const {
        theme,
        config
      } = this.props.configFn(day);
      return /*#__PURE__*/React.createElement(DayOfWeek, {
        key: idx,
        dayIdx: idx,
        asStartOfDay: this.asStartOfDay,
        onTimelineSelect: config.canCreate ? this.props.onTimeSelect : undefined,
        style: bgStyleTheme(get(theme, 'day.background'))
      }, this.createEvents(day, events));
    }));
  }
}
EventsWeekView.propTypes = {
  onTimeSelect: PropTypes.func,
  events: PropTypes.array.isRequired,
  focusDate: PropTypes.object.isRequired,
  eventComponent: PropTypes.func.isRequired,
  configFn: PropTypes.func.isRequired
};
var EventsWeekView$1 = withConfigForTime(EventsWeekView);

class WeekView extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    onTimeSelect: PropTypes.func,
    events: PropTypes.array.isRequired,
    focusDate: PropTypes.object.isRequired,
    eventComponent: PropTypes.func.isRequired,
    secondarySortFn: PropTypes.func
  };
  scrollToMorning() {
    const EIGHT_AM = 8;
    const HOUR_HEIGHT = 50;
    const POSITION = HOUR_HEIGHT * EIGHT_AM;
    this.calendarRef.scrollTop = POSITION;
  }
  componentDidMount() {
    this.scrollToMorning();
  }
  render() {
    return /*#__PURE__*/React.createElement("div", {
      className: cn('week-view-comp', this.props.className)
    }, /*#__PURE__*/React.createElement("div", {
      className: "calendar"
    }, /*#__PURE__*/React.createElement(WeekCalendarHeader, {
      focusDate: this.props.focusDate,
      events: this.props.events,
      secondarySortFn: this.props.secondarySortFn,
      eventComponent: this.props.eventComponent
    }), /*#__PURE__*/React.createElement("div", {
      className: "calendar-body",
      ref: ref => this.calendarRef = ref
    }, /*#__PURE__*/React.createElement("div", {
      className: "time-line-labels-wrapper"
    }, /*#__PURE__*/React.createElement(TimeLineLabels, null)), /*#__PURE__*/React.createElement("div", {
      className: "events-week-view-wrapper"
    }, /*#__PURE__*/React.createElement(EventsWeekView$1, {
      events: this.props.events,
      onTimeSelect: this.props.onTimeSelect,
      focusDate: this.props.focusDate,
      eventComponent: this.props.eventComponent
    })))));
  }
}

class FocusControl extends React.Component {
  oneWeekPrior() {
    const newFocus = moment(this.props.focusDate).subtract(7, 'days');
    this.props.onFocusDateChange(newFocus);
  }
  oneWeekAhead() {
    const newFocus = moment(this.props.focusDate).add(7, 'days');
    this.props.onFocusDateChange(newFocus);
  }
  render() {
    const startOf = moment(this.props.focusDate).startOf('week').format('MMM DD, YYYY');
    const endOf = moment(this.props.focusDate).endOf('week').format('MMM DD, YYYY');
    return /*#__PURE__*/React.createElement(AbstractFocusControl, {
      className: "week-focus-control-comp",
      priorElem: this.props.priorElem,
      aheadElem: this.props.aheadElem,
      onGoPrior: () => this.oneWeekPrior(),
      onGoAhead: () => this.oneWeekAhead()
    }, /*#__PURE__*/React.createElement("span", {
      className: "start-of"
    }, startOf), /*#__PURE__*/React.createElement("span", null, "\u2013"), /*#__PURE__*/React.createElement("span", {
      className: "end-of"
    }, endOf));
  }
}
FocusControl.propTypes = {
  focusDate: PropTypes.object.isRequired,
  priorElem: PropTypes.node.isRequired,
  aheadElem: PropTypes.node.isRequired,
  onFocusDateChange: PropTypes.func.isRequired
};

const Week = {
  View: WeekView,
  FocusControl: FocusControl
};
const Month = {
  View: MonthView,
  FocusControl: FocusControl$1
};

export { index as AbstractAgenda, index$1 as AbstractEvent, ControlPanel, EventDetailsSection, EventDetailsTitle, EventDuration, LensControl, Month, OrganizationAvatar, Provider, Week };
//# sourceMappingURL=index.js.map
